import { isPriceKey } from '../../lib/currency';

const FLOOR_KEYS = ['piso', 'nivel', 'floor'];
const STATUS_KEYS = ['estatus', 'estado', 'status'];
const COLOR_KEYS = ['color'];
const DETAIL_KEYS = ['detalle', 'descripcion', 'description'];
const SUMMARY_HIDDEN_KEYS = new Set([
  ...FLOOR_KEYS,
  ...STATUS_KEYS,
  ...COLOR_KEYS,
  'numero',
  'numerohabitacion',
]);

export function normalizeKey(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

function findFeature(features, aliases) {
  return features.find((feature) => {
    const key = normalizeKey(feature?.atributo);
    return aliases.some((alias) => key === alias || key.includes(alias));
  });
}

function normalizeUnitNumber(value) {
  const text = String(value ?? '').trim();
  const numericMatch = text.match(/\d+/);
  return numericMatch ? String(Number(numericMatch[0])) : text;
}

export function toAvailabilityItem(item) {
  const features = Array.isArray(item?.features) ? item.features : [];
  const floorFeature = findFeature(features, FLOOR_KEYS);
  const statusFeature = findFeature(features, STATUS_KEYS);
  const colorFeature = findFeature(features, COLOR_KEYS);
  const detailFeature = findFeature(features, DETAIL_KEYS);
  const priceFeature = features.find((feature) => isPriceKey(feature?.atributo));
  const number = normalizeUnitNumber(item?.titulo);
  const summary = features.filter((feature) => {
    const key = normalizeKey(feature?.atributo);
    return !SUMMARY_HIDDEN_KEYS.has(key) && !isPriceKey(key);
  });

  return {
    id: item?.documentId || item?.id || number,
    source: item,
    number,
    title: item?.titulo || `Departamento ${number}`,
    floor: normalizeUnitNumber(floorFeature?.valor),
    status: String(statusFeature?.valor || '').trim(),
    color: String(colorFeature?.valor || '').trim(),
    detail: String(detailFeature?.valor || '').trim(),
    price: priceFeature?.valor ?? '',
    summary,
  };
}

function findFractionArray(value, depth = 0) {
  if (depth > 4 || value == null) return null;

  if (Array.isArray(value)) {
    if (!value.length) return value;
    const looksLikeFractions = value.some((entry) => entry && typeof entry === 'object');
    return looksLikeFractions ? value : null;
  }

  if (typeof value !== 'object') return null;

  for (const [key, child] of Object.entries(value)) {
    if (normalizeKey(key).includes('fraccion') && Array.isArray(child)) return child;
  }

  for (const child of Object.values(value)) {
    const result = findFractionArray(child, depth + 1);
    if (result) return result;
  }

  return null;
}

function firstValue(record, aliases) {
  if (!record || typeof record !== 'object') return '';
  const entry = Object.entries(record).find(([key]) => {
    const normalized = normalizeKey(key);
    return aliases.some((alias) => normalized === alias || normalized.includes(alias));
  });
  return entry?.[1] ?? '';
}

function isEnabledStatus(status, rawEnabled) {
  if (typeof rawEnabled === 'boolean') return rawEnabled;
  if (typeof rawEnabled === 'number') return rawEnabled === 1;

  const normalized = normalizeKey(status || rawEnabled);
  if (!normalized) return true;
  return !['vendido', 'no disponible', 'inhabilitado', 'apartado', 'bloqueado', 'false', '0']
    .some((word) => normalized.includes(word));
}

function buildAggregateFractions(source) {
  if (!source || typeof source !== 'object' || Array.isArray(source)) return [];

  const departmentStatus = String(source.estatus || source.estado || source.status || '').trim();
  const departmentColor = String(source.color || '').trim();
  const departmentCurrency = String(source.moneda || source.currency || 'MXN').trim().toUpperCase();

  const groups = Object.entries(source).filter(([key, value]) => {
    if (normalizeKey(key) === 'disponibilidad') return false;
    return value
      && typeof value === 'object'
      && !Array.isArray(value)
      && Number.isFinite(Number(value.total));
  });

  const fallbackAvailability = source.disponibilidad;
  if (!groups.length && Number.isFinite(Number(fallbackAvailability?.total))) {
    groups.push(['fracción', {
      total: fallbackAvailability.total,
      disponibles: fallbackAvailability.disponibles,
      precio: source.precio,
    }]);
  }

  let fractionNumber = 0;
  return groups.flatMap(([groupName, group]) => {
    const total = Math.max(0, Number(group.total) || 0);
    const available = Math.max(0, Number(group.disponibles) || 0);
    const displayGroup = String(groupName)
      .replace(/([a-záéíóúñ])([A-ZÁÉÍÓÚÑ])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .trim();

    return Array.from({ length: total }, (_, index) => {
      fractionNumber += 1;
      const enabled = index < available;
      return {
        id: `aggregate-${groupName}-${fractionNumber}`,
        number: String(fractionNumber),
        status: departmentStatus || (enabled ? 'Disponible' : 'No disponible'),
        color: departmentColor,
        currency: departmentCurrency,
        enabled,
        info: displayGroup.charAt(0).toUpperCase() + displayGroup.slice(1),
        price: group.precio ?? source.precio ?? '',
        startDate: '',
        endDate: '',
        facts: [],
        source: group,
      };
    });
  });
}

function getFractionFacts(fraction) {
  const reserved = [
    'id', 'numero', 'fraccion', 'estatus', 'estado', 'status', 'habilitado',
    'disponible', 'enabled', 'detalle', 'descripcion', 'info', 'tipo',
    'precio', 'importe', 'monto', 'price', 'fecha inicio', 'fecha fin',
    'inicio', 'fin', 'desde', 'hasta', 'start', 'end',
  ];

  return Object.entries(fraction || {})
    .filter(([key, value]) => {
      const normalized = normalizeKey(key);
      return !reserved.some((entry) => normalized === entry)
        && value !== null
        && ['string', 'number', 'boolean'].includes(typeof value)
        && String(value).trim() !== '';
    })
    .map(([label, value]) => ({ label, value: String(value) }));
}

export function normalizeDepartmentDetail(payload) {
  const source = payload?.result ?? payload ?? {};
  const fractionRows = findFractionArray(source);
  const departmentStatus = String(source?.estatus || source?.estado || source?.status || '').trim();
  const departmentColor = String(source?.color || '').trim();
  const departmentCurrency = String(source?.moneda || source?.currency || 'MXN').trim().toUpperCase();

  if (!fractionRows) {
    return { source, fractions: buildAggregateFractions(source) };
  }

  return {
    source,
    fractions: fractionRows.map((fraction, index) => {
      const number = firstValue(fraction, ['numero fraccion', 'fraccion', 'numero', 'id']) || index + 1;
      const status = firstValue(fraction, ['estatus', 'estado', 'status']);
      const enabledValue = firstValue(fraction, ['habilitado', 'disponible', 'enabled']);

      return {
        id: fraction?.id || `${number}-${index}`,
        number: String(number),
        status: String(status || departmentStatus || (isEnabledStatus(status, enabledValue) ? 'Disponible' : 'No disponible')),
        color: String(firstValue(fraction, ['color']) || departmentColor),
        currency: String(firstValue(fraction, ['moneda', 'currency']) || departmentCurrency).toUpperCase(),
        enabled: isEnabledStatus(status, enabledValue),
        info: String(firstValue(fraction, ['detalle', 'descripcion', 'info', 'tipo']) || 'Info'),
        price: firstValue(fraction, ['precio', 'importe', 'monto', 'price']),
        startDate: firstValue(fraction, ['fecha inicio', 'inicio', 'desde', 'start']),
        endDate: firstValue(fraction, ['fecha fin', 'fin', 'hasta', 'end']),
        facts: getFractionFacts(fraction),
        source: fraction,
      };
    }),
  };
}
